import React, { useState, useRef, useEffect, useCallback } from "react";

// Screens slide inside a fixed phone frame, so nothing jumps between steps —
// the frame stays put and only the screen behind it moves.
const AppGallery = ({ items = [], size = "default", frame = "phone" }) => {
  const [index, setIndex] = useState(0);
  const touchStartX = useRef(null);
  const count = items.length;

  const wrap = (n) => ((n % count) + count) % count;
  // functional update so rapid clicks each advance a slide instead of
  // all resolving against the same rendered index
  const step = useCallback(
    (delta) => setIndex((i) => (((i + delta) % count) + count) % count),
    [count]
  );
  const goTo = useCallback((n) => setIndex(wrap(n)), [count]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowLeft") step(-1);
      if (e.key === "ArrowRight") step(1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [step]);

  if (!count) return null;

  const large = size === "large";
  const isWeb = frame === "browser";

  // phones are 9:19.5; the web captures are 1180x738
  const screenW = isWeb ? (large ? 860 : 620) : large ? 300 : 252;
  const screenH = Math.round(screenW * (isWeb ? 0.625 : 2.16));
  const framePad = isWeb ? 16 : 20; // matches p-2 / p-[10px] on the frame

  const current = items[index];

  return (
    <div className="flex flex-col items-center w-full select-none">
      <div className="relative flex items-center justify-center w-full">
        {/* Prev */}
        <button
          onClick={() => step(-1)}
          aria-label="Previous screen"
          className="absolute left-0 -translate-x-3 sm:-translate-x-14 z-20 w-11 h-11 rounded-full
                     bg-ink-card border border-ink-line text-paper cursor-pointer
                     flex items-center justify-center hover:bg-brand hover:border-brand
                     hover:scale-105 active:scale-95 transition-all duration-200
                     focus-visible:ring-2 focus-visible:ring-brand-cyan"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        {/* Device frame */}
        <div
          className={`relative bg-[#111119] border border-ink-line shadow-phone shrink ${
            isWeb ? "rounded-xl p-2 pt-7" : "rounded-[2.2rem] p-[10px]"
          }`}
          style={{ width: screenW + framePad, maxWidth: "100%" }}
          onTouchStart={(e) => (touchStartX.current = e.touches[0].clientX)}
          onTouchEnd={(e) => {
            if (touchStartX.current === null) return;
            const dx = e.changedTouches[0].clientX - touchStartX.current;
            if (Math.abs(dx) > 45) step(dx < 0 ? 1 : -1);
            touchStartX.current = null;
          }}
        >
          {/* browser chrome dots */}
          {isWeb && (
            <div className="absolute top-2.5 left-3 flex gap-1.5">
              <span className="w-2 h-2 rounded-full bg-white/20" />
              <span className="w-2 h-2 rounded-full bg-white/20" />
              <span className="w-2 h-2 rounded-full bg-white/20" />
            </div>
          )}

          {/* screen */}
          <div
            className={`relative overflow-hidden bg-black max-w-full ${
              isWeb ? "rounded-md" : "rounded-[1.7rem]"
            }`}
            // aspect-ratio drives the height so a wide web capture can shrink
            // on small screens without distorting
            style={{ width: "100%", aspectRatio: `${screenW} / ${screenH}` }}
          >
            <div
              className="flex h-full transition-transform duration-500 ease-[cubic-bezier(0.4,0,0.2,1)]"
              style={{
                width: `${count * 100}%`,
                transform: `translateX(-${index * (100 / count)}%)`,
              }}
            >
              {items.map((it, i) => (
                <div
                  key={i}
                  className="h-full shrink-0"
                  style={{ width: `${100 / count}%` }}
                >
                  <img
                    src={it.img}
                    alt={it.title}
                    draggable="false"
                    loading={i === 0 ? "eager" : "lazy"}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* notch */}
          {!isWeb && (
            <div className="absolute top-[18px] left-1/2 -translate-x-1/2 w-20 h-[18px] bg-[#111119] rounded-full" />
          )}
        </div>

        {/* Next */}
        <button
          onClick={() => step(1)}
          aria-label="Next screen"
          className="absolute right-0 translate-x-3 sm:translate-x-14 z-20 w-11 h-11 rounded-full
                     bg-ink-card border border-ink-line text-paper cursor-pointer
                     flex items-center justify-center hover:bg-brand hover:border-brand
                     hover:scale-105 active:scale-95 transition-all duration-200
                     focus-visible:ring-2 focus-visible:ring-brand-cyan"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {/* Caption — explains the screen you're looking at */}
      <div
        key={index}
        className="mt-7 text-center max-w-sm px-2 animate-galleryFade"
        style={{ minHeight: 92 }}
      >
        <p className="font-mono text-[10px] tracking-[0.22em] text-brand-cyan mb-2">
          {String(index + 1).padStart(2, "0")} / {String(count).padStart(2, "0")}
        </p>
        <h4 className="font-display text-[24px] leading-none tracking-wide text-paper mb-2.5">
          {current.title}
        </h4>
        <p className="text-sm text-muted leading-relaxed">
          {current.caption}
        </p>
      </div>

      {/* Progress dots */}
      <div className="flex items-center gap-2 mt-5">
        {items.map((_, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            aria-label={`Go to screen ${i + 1}`}
            className={`h-3 rounded-full transition-all duration-300 cursor-pointer ${
              i === index
                ? "w-8 bg-brand"
                : "w-3 bg-ink-line hover:bg-muted"
            }`}
          />
        ))}
      </div>
    </div>
  );
};

export default AppGallery;
